/**
 * Обращения к API.
 *
 * Адрес задаётся при сборке через VITE_API_URL. Локально переменной нет, и
 * запросы идут на /api — их перехватывает прокси Vite и уводит на контейнер.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';

export type Lang = 'en' | 'ru' | 'hy';
export type Currency = 'AMD' | 'USD';

export interface Category {
  id: string;
  slug: string;
  name: string;
  productsCount: number;
}

export interface Variant {
  id: string;
  sku: string;
  name: string;
  weightG: number | null;
  isWhole: boolean;
  price: number;
  compareAt: number | null;
  inStock: boolean;
}

export interface Product {
  id: string;
  slug: string;
  kind: 'SIMPLE' | 'BUNDLE' | 'WARE';
  title: string;
  short: string | null;
  description?: string | null;
  brewing?: string | null;
  tastingNotes?: string[];
  origin?: string | null;
  region?: string | null;
  harvestYear?: number | null;
  category: { slug: string; name: string } | null;
  image: string | null;
  /** Все фото по порядку, первое — главное. Есть только в карточке товара */
  images?: Array<{ url: string; alt: string | null }>;
  /** Валюта цен: AMD — драмы, USD — центы */
  currency?: Currency;
  priceFrom: number | null;
  inStock: boolean;
  variants: Variant[];
}

export type ItemIssue =
  | 'OUT_OF_STOCK'
  | 'QTY_REDUCED'
  | 'PRICE_CHANGED'
  | 'INACTIVE'
  | 'CURRENCY_UNAVAILABLE';

export interface PricedItem {
  variantId: string;
  sku: string;
  title: string;
  variantName: string;
  unitPrice: number;
  qty: number;
  lineTotal: number;
  available: number;
  issue: ItemIssue | null;
}

export interface PricedCart {
  currency: Currency;
  items: PricedItem[];
  subtotal: number;
  discount: number;
  delivery: number;
  total: number;
  issues: ItemIssue[];
  minOrder: { required: number | null; met: boolean };
  deliveryQuotedSeparately: boolean;
}

export interface ShopSettings {
  minOrder: Record<Currency | 'RUB', number | null>;
  delivery: Array<{
    method: 'PICKUP' | 'YEREVAN_COURIER' | 'ARMENIA_POST' | 'INTERNATIONAL';
    price: Record<Currency | 'RUB', number | null>;
    freeFrom: Record<Currency | 'RUB', number | null>;
    label: { ru: string; en: string };
    quotedSeparately: boolean;
  }>;
}

export interface CreatedOrder {
  id: string;
  number: string;
  status: string;
  paymentStatus: string;
  currency: Currency;
  total: number;
  createdAt: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Снимок каталога, который сборка кладёт прямо в страницу.
 *
 * Сервер API на бесплатном тарифе засыпает и просыпается до минуты. Чтобы
 * человек и поисковик не смотрели всё это время на пустые карточки, витрина
 * ждёт живой ответ пару секунд, а потом показывает снимок со сборки. Живой
 * запрос при этом не отменяется, и следующие переходы берут свежие данные.
 * Корзина и заказ снимком не пользуются: цены и остатки сверяет сервер.
 */
const SNAPSHOT_WAIT_MS = 2500;

export function snapshotKey(lang: Lang, path: string): string {
  return `${lang} ${path}`;
}

declare global {
  interface Window {
    __VISHAP_SNAPSHOT__?: Record<string, unknown>;
  }
}

function withSnapshot<T>(live: Promise<T>, key: string): Promise<T> {
  const snapshot =
    typeof window === 'undefined' ? undefined : window.__VISHAP_SNAPSHOT__?.[key];
  if (snapshot === undefined) return live;

  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => resolve(snapshot as T), SNAPSHOT_WAIT_MS);
    live.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(snapshot as T);
      },
    );
  });
}

/** Адреса каталога. Их же запрашивает сборка, когда делает снимок */
export const paths = {
  categories: '/v1/categories',
  products: (params: { category?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.category) q.set('category', params.category);
    if (params.search) q.set('search', params.search);
    q.set('limit', '100');
    return `/v1/products?${q}`;
  },
  product: (slug: string) => `/v1/products/${slug}`,
};

/**
 * Снимок сборки сделан в драмах, поэтому выручает только драмовую витрину.
 * В долларах ждём живой ответ: лучше подождать, чем показать драмы со знаком $
 */
function cached<T>(path: string, lang: Lang, currency: Currency): Promise<T> {
  const live = request<T>(path, lang, { headers: { 'x-currency': currency } });
  return currency === 'AMD' ? withSnapshot(live, snapshotKey(lang, path)) : live;
}

async function request<T>(
  path: string,
  lang: Lang,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const { json, ...rest } = init ?? {};

  const response = await fetch(`${BASE}/api${path}`, {
    ...rest,
    headers: {
      'accept-language': lang,
      ...(json ? { 'content-type': 'application/json' } : {}),
      ...rest.headers,
    },
    ...(json ? { body: JSON.stringify(json) } : {}),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const message = Array.isArray(body?.message)
      ? body.message.join('. ')
      : (body?.message ?? `Ошибка ${response.status}`);
    throw new ApiError(message, response.status, body);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  settings: (lang: Lang) => request<ShopSettings>('/v1/settings', lang),

  categories: (lang: Lang) => cached<{ items: Category[] }>(paths.categories, lang, 'AMD'),

  products: (
    lang: Lang,
    currency: Currency,
    params: { category?: string; search?: string } = {},
  ) => cached<{ items: Product[]; total: number }>(paths.products(params), lang, currency),

  product: (lang: Lang, currency: Currency, slug: string) =>
    cached<Product>(paths.product(slug), lang, currency),

  validateCart: (
    lang: Lang,
    body: {
      items: Array<{ variantId: string; qty: number }>;
      currency: Currency;
      deliveryMethod: string;
      promoCode?: string;
    },
  ) => request<PricedCart>('/v1/cart/validate', lang, { method: 'POST', json: body }),

  createOrder: (lang: Lang, body: unknown, idempotencyKey: string) =>
    request<CreatedOrder>('/v1/orders', lang, {
      method: 'POST',
      json: body,
      headers: { 'idempotency-key': idempotencyKey },
    }),

  order: (lang: Lang, number: string, phoneTail: string) =>
    request<Record<string, unknown>>(
      `/v1/orders/${encodeURIComponent(number)}?phone=${encodeURIComponent(phoneTail)}`,
      lang,
    ),
};
